/**
 * @file People (`#/people`) — admin global directory.
 *
 * The IT-side answer to "who works here and where are they in the
 * lifecycle". Filter tabs (Active / Onboarding / Offboarding / All) live on
 * top — Active is the default since that's the daily working set;
 * All sits at the end and is the "audit" view (includes long-offboarded
 * people from years ago). Rows click through to /people/:id.
 *
 * Devices column counts physical devices held (from /devices via
 * allHolderIds — includes Meraki-held devices that don't have an Abra
 * holder) PLUS active phone tariffs (Bamboo customAssets where
 * dateReturned is null). Sorting by Devices descending in the All tab
 * surfaces ex-employees who still have gear assigned to them.
 */

import { el, clear, formatDate } from '../utils.js';
import { Icons }       from '../icons.js';
import { PHASES }      from '../config.js';
import { navigate }    from '../router.js';
import { onSnapshot, collection } from 'firebase/firestore';
import { renderLoadError } from '../lib/load-error.js';
import { copyable } from '../lib/copyable.js';
import { phoneDigits, matchSimNumber, looksLikePhoneQuery } from '../lib/phone-match.js';
import { db }          from '../firebase.js';
import { renderPageHead } from '../page-header.js';
import { testCollectionName } from '../test-mode.js';
import { sortableTable, listCard } from '../lib/sortable-table.js';
import { loadListState, saveListState, setListContext } from '../lib/list-memory.js';

const TABS = [
  { id: 'active',      label: 'Active',      match: (e) => e.phase === PHASES.active },
  { id: 'onboarding',  label: 'Onboarding',  match: (e) => e.phase === PHASES.onboarding },
  { id: 'offboarding', label: 'Offboarding', match: (e) => e.phase === PHASES.offboarding },
  { id: 'all',         label: 'All',         match: () => true },
];

export function renderPeople(host) {
  clear(host);

  host.appendChild(renderPageHead(
    'People',
    'Everyone in BambooHR. Click a row to see their gear and history. Use “All” to spot ex-employees who still have devices assigned.',
  ));

  // Default to the working set, not the historic dump. Tab, search and sort
  // are remembered per browser tab so coming back from a person lands on
  // the same list (and Prev / Next on the person walk that same order).
  const state = loadListState('people', { tab: 'active', search: '', sortIndex: 0, sortDir: 'asc' });
  if (!TABS.some((t) => t.id === state.tab)) state.tab = 'active';

  // Tab strip — use data-tab for the active highlight rather than text
  // matching (the previous textContent check would collide if "Active"
  // ever shared a string with another tab).
  const tabsEl = el('div', { class: 'segment' },
    ...TABS.map((t) => el('button', {
      'data-tab': t.id,
      class: t.id === state.tab ? 'active' : '',
      onClick: () => {
        state.tab = t.id;
        saveListState('people', { tab: t.id });
        for (const btn of tabsEl.querySelectorAll('button')) {
          btn.classList.toggle('active', btn.dataset.tab === t.id);
        }
        rerender();
      },
    }, t.label)),
  );

  const searchInput = el('input', { type: 'search', placeholder: 'Search name, email or phone number…' });
  searchInput.value = state.search;
  searchInput.addEventListener('input', () => {
    state.search = searchInput.value.trim().toLowerCase();
    saveListState('people', { search: state.search });
    rerender();
  });

  host.appendChild(el('div', { class: 'toolbar' }, tabsEl, searchInput));

  const tableHost = el('div', { class: 'card', style: 'padding:0' });
  host.appendChild(tableHost);
  // A watch failure used to leave this page on its skeleton forever with only
  // a console error — no signal, no retry.
  const showLoadError = () => {
    clear(tableHost);
    tableHost.appendChild(renderLoadError('Could not load people.'));
  };

  // Two live sources joined client-side: /employees for the directory and
  // /devices for the per-person device count. Phone-tariff counts come from
  // /employees.assets directly (Bamboo customAssets — no separate query).
  let rows = [];
  const deviceCountByEmployeeId = new Map();
  // Laptops / phones per person — the "still has the old one next to the
  // new one" check. Anything above one per category is highlighted.
  const categoryCountByEmployeeId = new Map();
  const NONE = Object.freeze({ laptop: 0, phone: 0 });

  function deviceCountFor(row) {
    const physical = deviceCountByEmployeeId.get(row.id) || 0;
    const activeSimCards = (row.assets || [])
      .filter((a) => a.category === 'Sim Card' && !a.dateReturned)
      .length;
    return physical + activeSimCards;
  }
  const categoryCountsFor = (row) => categoryCountByEmployeeId.get(row.id) || NONE;
  // A count cell: muted zero, plain one, orange when someone holds more
  // than one laptop or phone.
  const countCell = (n) => el('span', {
    class: n ? null : 'muted',
    style: `font-variant-numeric:tabular-nums${n > 1 ? ';color:var(--color-error);font-weight:600' : ''}`,
  }, String(n));


  function rerender() {
    clear(tableHost);
    const tab = TABS.find((t) => t.id === state.tab) || TABS[0];
    const filtered = rows.filter((r) => {
      if (!tab.match(r)) return false;
      if (state.search) {
        const hay = `${r.fullName || ''} ${r.email || ''} ${r.jobTitle || ''} ${r.department || ''}`.toLowerCase();
        // Fall back to a SIM-number match (digits, format-agnostic) so
        // "who holds 774 833 785?" works here too — see lib/phone-match.js.
        if (!hay.includes(state.search)) {
          if (!(looksLikePhoneQuery(state.search) && matchSimNumber(r, phoneDigits(state.search)))) return false;
        }
      }
      return true;
    });
    if (!filtered.length) {
      tableHost.appendChild(el('div', { class: 'empty' },
        Icons.user(),
        el('p', null, rows.length ? 'Nobody matches those filters.' : 'No people on record.'),
        el('p', { class: 'muted' }, rows.length ? 'Loosen the filters or clear the search.' : 'Run a sync from Settings to pull from Bamboo.'),
      ));
      return;
    }
    const renderPersonRow = (r) => {
      const count = deviceCountFor(r);
      const cats  = categoryCountsFor(r);
      // Light highlight on rows where someone who shouldn't have a device
      // still has one — i.e. phase == done with count > 0.
      const flagged = r.phase === PHASES.done && count > 0;
      return el('tr', {
        onClick: () => navigate(`/people/${r.id}`),
        class: flagged ? 'row-warn' : null,
        'data-row-link': true,
      },
        el('td', null, r.fullName ? copyable(r.fullName, { label: 'name' }) : '-'),
        el('td', { class: 'muted' }, r.email ? copyable(r.email, { label: 'email' }) : '-'),
        el('td', { class: 'muted' }, r.department || '-'),
        el('td', null, el('span', { class: `chip ${phaseClass(r.phase)}` }, phaseLabel(r.phase))),
        el('td', null,
          count > 0
            ? el('span', {
                style: `font-variant-numeric:tabular-nums${flagged ? ';color:var(--color-error);font-weight:600' : ''}`,
              }, String(count))
            : el('span', { class: 'muted', style: 'font-variant-numeric:tabular-nums' }, '0'),
        ),
        el('td', null, countCell(cats.laptop)),
        el('td', null, countCell(cats.phone)),
        el('td', { class: 'muted' },
          r.phase === PHASES.offboarding ? formatDate(r.endDate) : formatDate(r.startDate),
        ),
      );
    };
    // Phone card: who, how to reach them, where they are in the lifecycle,
    // how much gear they hold.
    const renderPersonCard = (r) => {
      const count = deviceCountFor(r);
      const cats  = categoryCountsFor(r);
      const flagged = r.phase === PHASES.done && count > 0;
      const catNote = (n, word) => (n ? el('span', n > 1 ? { style: 'color:var(--color-error)' } : null, `${n} ${word}${n === 1 ? '' : 's'}`) : null);
      return listCard({
        title: r.fullName || '-',
        meta:  [r.email, r.department, catNote(cats.laptop, 'laptop'), catNote(cats.phone, 'phone'),
                r.phase === PHASES.offboarding ? `Ends ${formatDate(r.endDate)}` : null],
        aside: [
          el('span', { class: `chip ${phaseClass(r.phase)}` }, phaseLabel(r.phase)),
          el('span', {
            class: count ? null : 'muted',
            style: `font-variant-numeric:tabular-nums${flagged ? ';color:var(--color-error);font-weight:600' : ''}`,
          }, `${count} device${count === 1 ? '' : 's'}`),
        ],
        onClick: () => navigate(`/people/${r.id}`),
        class: flagged ? 'row-warn' : '',
      });
    };
    tableHost.appendChild(sortableTable({
      columns: [
        { label: 'Name',        sortValue: (r) => r.fullName },
        { label: 'Email',       sortValue: (r) => r.email },
        { label: 'Department',  sortValue: (r) => r.department },
        { label: 'Phase',       sortValue: (r) => phaseLabel(r.phase) },
        { label: 'Devices',     sortValue: (r) => deviceCountFor(r) },
        { label: 'Laptops',     sortValue: (r) => categoryCountsFor(r).laptop },
        { label: 'Phones',      sortValue: (r) => categoryCountsFor(r).phone },
        { label: 'Start / End', sortValue: (r) => (r.phase === PHASES.offboarding ? r.endDate : r.startDate) },
      ],
      rows: filtered,
      renderRow: renderPersonRow,
      renderCard: renderPersonCard,
      initialSort: { index: state.sortIndex, dir: state.sortDir },
      onOrder: ({ rows: ordered, sortIndex, sortDir }) => {
        state.sortIndex = sortIndex; state.sortDir = sortDir;
        saveListState('people', { sortIndex, sortDir });
        setListContext({ source: 'people', ids: ordered.map((r) => r.id), href: '#/people', label: 'People' });
      },
    }));
  }

  // /employees — directory + per-row asset arrays (phone tariffs).
  const unsubEmployees = onSnapshot(
    collection(db, testCollectionName('employees')),
    (snap) => {
      rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rerender();
    },
    (err) => { console.error('[people] employees watch error', err); showLoadError(); },
  );
  // /devices — count by allHolderIds. A device with a holder mismatch gets
  // counted on both employees' rows, matching the /people/:id view.
  const unsubDevices = onSnapshot(
    collection(db, testCollectionName('devices')),
    (snap) => {
      deviceCountByEmployeeId.clear();
      categoryCountByEmployeeId.clear();
      for (const d of snap.docs) {
        const data = d.data();
        const ids = Array.isArray(data.allHolderIds) ? data.allHolderIds
                  : (data.currentHolder ? [data.currentHolder] : []);
        for (const id of ids) {
          deviceCountByEmployeeId.set(id, (deviceCountByEmployeeId.get(id) || 0) + 1);
          if (data.category === 'laptop' || data.category === 'phone') {
            const c = categoryCountByEmployeeId.get(id) || { laptop: 0, phone: 0 };
            c[data.category] += 1;
            categoryCountByEmployeeId.set(id, c);
          }
        }
      }
      rerender();
    },
    (err) => { console.error('[people] devices watch error', err); showLoadError(); },
  );
  host.addEventListener('cleanup', () => { unsubEmployees(); unsubDevices(); }, { once: true });
}

function phaseLabel(p) {
  switch (p) {
    case PHASES.onboarding:  return 'Onboarding';
    case PHASES.active:      return 'Active';
    case PHASES.offboarding: return 'Offboarding';
    case PHASES.done:        return 'Done';
    default:                 return p || '-';
  }
}

function phaseClass(p) {
  switch (p) {
    case PHASES.onboarding:  return 'chip-warning';
    case PHASES.active:      return 'chip-ready';
    case PHASES.offboarding: return 'chip-error';
    case PHASES.done:        return 'chip-idle';
    default:                 return 'chip-idle';
  }
}
